/*
  tt-dealer-map.js - the dealer locator's list and map, driven by one dataset.

  The list and the pins read the SAME array and are re-rendered together, so they
  cannot drift. Everything the mockup does interactively is here: free-text
  search, "use my location", distance ranking, pin/row selection, and reset.

  Row markup is the mockup's `<sc-for>` body reproduced node for node, including
  the inline styles, because the mockup carries its design in inline styles and
  the fidelity harness diffs computed values against it.

  Search is offline and deliberately so - this theme calls no geocoding service
  at render time (design/zola/data-model.md). A typed query matches on name,
  city, state and ZIP; "use my location" uses the browser's own geolocation,
  which is a permission prompt, not a service call. Distance is great-circle
  from whatever origin is active.
*/
(function () {
  'use strict';

  var MILES_PER_RADIAN = 3958.7613;
  var root, mapEl, rowsEl, listHeadingEl, distHeadingEl, resultLineEl, farNoteEl, searchEl;
  var dealers = [], map = null, markers = [], origin = null, selectedId = null;

  /* WHERE A TYPED ZIP OR "CITY, ST" IS (Josh, 2026-10-16: 92841 found "zero nearby"
     with two dealers half an hour away, because typing only matched text). The
     search now resolves the place from the theme's own US Census ZIP and city
     centroid files - assets/tt-geo-zip-<first digit>.json and
     tt-geo-place-<state>.json, still no outside service - and ranks EVERY dealer
     closest first. Dealers within NEAR_MILES are highlighted; beyond that the list
     still answers, with the nearest distance said out loud. */
  var NEAR_MILES = 100;
  var geoOrigin = null, typed = false, seq = 0, geoBase = '', geoCache = {};
  var STATES = { alabama: 'al', alaska: 'ak', arizona: 'az', arkansas: 'ar', california: 'ca', colorado: 'co', connecticut: 'ct', delaware: 'de', 'district of columbia': 'dc', florida: 'fl', georgia: 'ga', hawaii: 'hi', idaho: 'id', illinois: 'il', indiana: 'in', iowa: 'ia', kansas: 'ks', kentucky: 'ky', louisiana: 'la', maine: 'me', maryland: 'md', massachusetts: 'ma', michigan: 'mi', minnesota: 'mn', mississippi: 'ms', missouri: 'mo', montana: 'mt', nebraska: 'ne', nevada: 'nv', 'new hampshire': 'nh', 'new jersey': 'nj', 'new mexico': 'nm', 'new york': 'ny', 'north carolina': 'nc', 'north dakota': 'nd', ohio: 'oh', oklahoma: 'ok', oregon: 'or', pennsylvania: 'pa', 'puerto rico': 'pr', 'rhode island': 'ri', 'south carolina': 'sc', 'south dakota': 'sd', tennessee: 'tn', texas: 'tx', utah: 'ut', vermont: 'vt', virginia: 'va', washington: 'wa', 'west virginia': 'wv', wisconsin: 'wi', wyoming: 'wy' };
  var ABBR = {}; Object.keys(STATES).forEach(function (k) { ABBR[STATES[k]] = true; });

  function loadGeo(name) {
    if (!geoCache[name]) {
      geoCache[name] = fetch(geoBase + name).then(function (r) { return r.ok ? r.json() : {}; }).catch(function () { return {}; });
    }
    return geoCache[name];
  }
  function pt(v) { return v ? { lat: v[0] / 100, lng: v[1] / 100 } : null; }
  function normCity(c) { return c.toLowerCase().replace(/^st\.? /, 'saint ').replace(/-/g, ' ').replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim(); }

  /* A ZIP, ZIP+4, "City, ST", "City ST" or "City, State". Anything else is a
     dealer-name search, as before. */
  function resolvePlace(q) {
    if (!geoBase) return Promise.resolve(null);
    var m = q.match(/^\s*(\d{5})(?:-\d{4})?\s*$/);
    if (m) return loadGeo('tt-geo-zip-' + m[1].charAt(0) + '.json').then(function (t) { return pt(t[m[1]]); });
    // The last one to three words are the state (CA, Calif., North Carolina); the rest is the city.
    var words = q.toLowerCase().replace(/,/g, ' ').replace(/\s+/g, ' ').trim().split(' ');
    for (var n = Math.min(3, words.length - 1); n >= 1; n--) {
      var stw = words.slice(-n).join(' ').replace(/\./g, '');
      var st = ABBR[stw] ? stw : STATES[stw];
      if (st) {
        var city = normCity(words.slice(0, -n).join(' '));
        return loadGeo('tt-geo-place-' + st + '.json').then(function (t) { return pt(t[city]); });
      }
    }
    return Promise.resolve(null);
  }

  function parseDealers() {
    var tag = document.querySelector('script[type="application/json"][data-dealers]');
    if (!tag) return [];
    try {
      var parsed = JSON.parse(tag.textContent.trim() || '[]');
      return Array.isArray(parsed) ? parsed.filter(function (d) { return d && d.active !== false; }) : [];
    } catch (e) {
      // A malformed payload must not take the page down with it.
      if (window.console) console.error('[dealer-map] dealer JSON did not parse:', e);
      return [];
    }
  }

  function distanceMiles(a, b) {
    var toRad = Math.PI / 180;
    var dLat = (b.lat - a.lat) * toRad, dLng = (b.lng - a.lng) * toRad;
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(a.lat * toRad) * Math.cos(b.lat * toRad) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return MILES_PER_RADIAN * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }

  function hasPin(d) { return typeof d.lat === 'number' && typeof d.lng === 'number'; }

  function matches(d, q) {
    if (!q) return true;
    return (d.name + ' ' + (d.address || '') + ' ' + (d.country || '')).toLowerCase().indexOf(q) !== -1;
  }

  /* Rank: with an origin, nearest first; otherwise alphabetical, which is the
     order the projection already writes. */
  function ranked() {
    var q = (searchEl && searchEl.value || '').trim().toLowerCase();
    // A resolved place ranks every dealer; only a name search filters.
    var list = typed ? dealers.slice() : dealers.filter(function (d) { return matches(d, q); });
    if (origin) {
      list.forEach(function (d) { d._miles = hasPin(d) ? distanceMiles(origin, d) : Infinity; });
      list.sort(function (a, b) { return a._miles - b._miles; });
    } else {
      list.forEach(function (d) { delete d._miles; });
    }
    return list;
  }

  function el(tag, styles, text) {
    var n = document.createElement(tag);
    if (styles) n.setAttribute('style', styles);
    if (text != null) n.textContent = text;
    return n;
  }

  /* Display form of a url: no scheme, no trailing slash. */
  function websiteLabel(url) {
    return String(url).replace(/^https?:\/\//i, '').replace(/\/$/, '');
  }

  function directionsUrl(d) {
    var q = d.name + ' ' + (d.address || '').replace(/\n/g, ' ');
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(q);
  }

  function renderRows(list) {
    rowsEl.textContent = '';

    if (!list.length) {
      rowsEl.appendChild(el('p',
        'margin: 24px 0 0; font-size: 14px; color: var(--z-muted);',
        dealers.length ? 'No dealers match that search.' : 'No dealers are listed yet.'));
      return;
    }

    list.forEach(function (d, i) {
      var selected = d.id === selectedId;
      var row = el('div', 'display: flex; gap: 24px; padding: 24px 0; border-bottom: 1px solid var(--z-hairline); cursor: pointer; background: ' + (selected ? 'var(--z-tint-soft)' : 'transparent') + ';');
      row.setAttribute('data-dealer-id', d.id);

      /* No list numbers (Josh, 2026-10-29, both stores): a numbered list tells
         a visitor exactly how few dealers there are. */
      var body = el('div', 'display: flex; flex-direction: column; gap: 8px; min-width: 0; flex: 1;');
      var top = el('div', 'display: flex; align-items: baseline; justify-content: space-between; gap: 16px;');
      top.appendChild(el('span', "font-family: var(--z-font-display); font-weight: 700; font-size: 19px; letter-spacing: -0.01em;", d.name));
      var near = d._miles != null && d._miles <= NEAR_MILES;
      top.appendChild(el('span', 'font-size: 13px; font-weight: 600; color: ' + (near ? 'var(--z-amber)' : 'var(--z-muted)') + '; white-space: nowrap;',
        d._miles != null && isFinite(d._miles) ? Math.round(d._miles) + ' mi' : ''));
      body.appendChild(top);

      body.appendChild(el('span', 'font-size: 14px; color: var(--z-muted); line-height: 1.55; white-space: pre-line;', d.address || ''));

      /* Website sits above the phone number. Shopify holds no url for any dealer
         today, so in practice this renders for none of them - it appears the
         moment the projection has one, with no further change here. */
      var siteHref = d.website ? safeHref(d.website) : null;
      if (siteHref) {
        var site = document.createElement('a');
        site.href = siteHref;
        site.target = '_blank';
        site.rel = 'noopener';
        site.textContent = websiteLabel(d.website);
        site.setAttribute('style', 'font-size: 14px; color: var(--z-muted); text-decoration: underline; align-self: flex-start;');
        site.addEventListener('click', function (e) { e.stopPropagation(); });
        body.appendChild(site);
      }

      if (d.phone) body.appendChild(el('span', 'font-size: 14px; color: var(--z-muted);', d.phone));

      var dir = document.createElement('a');
      dir.href = directionsUrl(d);
      dir.target = '_blank';
      dir.rel = 'noopener';
      dir.textContent = 'Directions';
      dir.setAttribute('style', 'margin-top: 4px; font-size: 13px; font-weight: 600; border-bottom: 1px solid var(--z-ink); align-self: flex-start; padding-bottom: 2px;');
      dir.addEventListener('click', function (e) { e.stopPropagation(); });
      body.appendChild(dir);

      row.appendChild(body);
      row.addEventListener('click', function () { select(d.id, true); });
      rowsEl.appendChild(row);
    });
  }

  function renderHeadings(list) {
    listHeadingEl.textContent = origin ? 'Nearest dealers' : 'All dealers';
    distHeadingEl.textContent = origin ? 'Distance' : '';
    var within = origin ? list.filter(function (d) { return isFinite(d._miles) && d._miles <= NEAR_MILES; }).length : 0;
    resultLineEl.textContent = !dealers.length ? ''
      : origin && list.length && isFinite(list[0]._miles)
        ? (within ? within + (within === 1 ? ' dealer' : ' dealers') + ' within ' + NEAR_MILES + ' mi'
                  : 'Nearest dealer is ' + Math.round(list[0]._miles) + ' mi away')
        /* No standing count under the search (Josh, 2026-10-16, both stores):
           the line only answers a place search, nearest first. */
        : '';
    var far = origin && list.length && isFinite(list[0]._miles) && list[0]._miles > NEAR_MILES;
    farNoteEl.hidden = !(far || (dealers.length && !list.length));
  }

  /* Pins are the mockup's .zola-pin: an ink dot ringed in white, amber and
     scaled up when selected. No numeral (Josh, 2026-10-29, both stores):
     numbering showed how few dealers there are. Pin and row are tied by
     selection instead - click either and both light up amber.

     divIcon rather than circleMarker so the pin stays a styled DOM node like
     the mockup's. Every visual value lives in tt-dealer-map.css against tokens. */
  function renderMarkers(list) {
    if (!map) return;
    markers.forEach(function (m) { map.removeLayer(m); });
    markers = [];

    list.filter(hasPin).forEach(function (d) {
      var selected = d.id === selectedId;
      var marker = window.L.marker([d.lat, d.lng], {
        icon: window.L.divIcon({
          className: '',
          html: '<span class="zola-pin' + (selected ? ' is-active' : '') + '"></span>',
          iconSize: [16, 16],
          iconAnchor: [8, 8],
          popupAnchor: [0, -10],
        }),
        keyboard: false,
        title: d.name,
      }).addTo(map);
      marker.bindPopup(popupHtml(d));
      marker.on('click', function () { select(d.id, false); });
      marker._dealerId = d.id;
      markers.push(marker);
    });

    /* "Use my location" draws the visitor too - the mockup's .zola-origin, a
       white dot ringed amber. Without it the map re-sorts by distance from a
       point the visitor cannot see. */
    if (origin) {
      var me = window.L.marker([origin.lat, origin.lng], {
        icon: window.L.divIcon({
          className: '',
          html: '<span class="zola-origin"></span>',
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        }),
        keyboard: false,
        title: 'Your location',
      }).addTo(map);
      markers.push(me);
    }
  }

  /* Map popup: name, address, website, phone, and a Google Maps directions link
     that opens in a new tab (Josh, 2026-08-14). Built as a string because that
     is the only thing Leaflet's bindPopup takes; every interpolated value is
     escaped, and the url is escaped into an href that is only ever http(s) -
     see safeHref. */
  function popupHtml(d) {
    /* <b>, not <strong>: the mockup styles `.leaflet-popup-content b` as the
       block-level business name, and the styling lives in tt-dealer-map.css
       against tokens rather than inline here. */
    var parts = ['<b>' + escapeHtml(d.name) + '</b>'];

    if (d.address) parts.push('<span>' + escapeHtml(d.address).replace(/\n/g, '<br>') + '</span>');

    if (d.website) {
      var href = safeHref(d.website);
      if (href) {
        parts.push('<a href="' + escapeHtml(href) + '" target="_blank" rel="noopener" ' +
          'style="text-decoration: underline;">' + escapeHtml(websiteLabel(d.website)) + '</a>');
      }
    }

    /* `phone` is display text - "(909) 279-1722" - so it cannot also be the href.
       `phone_dial` carries E.164 for that. The fallback strips the display string
       for any record written before phone_dial existed; it dials correctly inside
       the NANP, which is every dealer today. */
    if (d.phone) {
      var tel = d.phone_dial || String(d.phone).replace(/[^\d+]/g, '');
      parts.push('<a href="tel:' + escapeHtml(tel) + '">' + escapeHtml(d.phone) + '</a>');
    }

    parts.push('<a href="' + escapeHtml(directionsUrl(d)) + '" target="_blank" rel="noopener" ' +
      'style="font-weight: 600; border-bottom: 1px solid currentColor; align-self: flex-start;">' +
      'Get directions</a>');

    return '<div style="display: flex; flex-direction: column; gap: 6px;">' + parts.join('') + '</div>';
  }

  /* Only ever emit an http(s) link. A javascript: or data: url in the dealer
     data would otherwise become a clickable script in the popup. */
  function safeHref(url) {
    var s = String(url).trim();
    if (/^https?:\/\//i.test(s)) return s;
    if (/^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(s)) return 'https://' + s;
    return null;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var firstFitDone = false;
  var startBounds = null;

  /* A FIXED OPENING FRAME (Cajo, Josh 2026-10-16: western North America to
     eastern Australia, Greenland to just under Australia). Fractional zoom so
     the frame fits exactly at any width, centred in projected space so its top
     and bottom edges are the ones asked for; never so far out that the world is
     shorter than the map, which leaves empty bands above and below. */
  function fitStart() {
    firstFitDone = true;
    var bz = map.getBoundsZoom(startBounds, false);
    var fillZ = Math.log(map.getSize().y / 256) / Math.LN2;
    var z = Math.max(bz, fillZ + 0.01);
    var a = map.project(startBounds.getNorthWest(), z), c = map.project(startBounds.getSouthEast(), z);
    var mid = a.add(c).divideBy(2);
    /* Where the map is too narrow for the frame (a phone), the floor above wins
       and the frame's own centre would leave the top of the world showing as a
       band; keep the view inside the world vertically. */
    var half = map.getSize().y / 2, worldH = 256 * Math.pow(2, z);
    mid.y = Math.min(Math.max(mid.y, half), worldH - half);
    map.setView(map.unproject(mid, z), z, { animate: false });
  }

  function fitTo(list) {
    if (!map) return;
    if (!firstFitDone && startBounds && !origin) return fitStart();
    var pts = list.filter(hasPin).map(function (d) { return [d.lat, d.lng]; });
    if (origin) pts.push([origin.lat, origin.lng]);
    /* The opening fit is instant: a zoom step requested while Leaflet is still
       animating the fit is dropped, so zoom_in below would never land. */
    var anim = { animate: firstFitDone };
    if (pts.length > 1) map.fitBounds(pts, { padding: [40, 40], maxZoom: 11, animate: firstFitDone });
    else if (pts.length === 1) map.setView(pts[0], 11, anim);
    /* THE OPENING VIEW ONLY can sit closer than "every dealer in frame": a store
       whose dealers span the globe opens on the whole world, poles and empty
       bands included (Cajo, Josh 2026-10-16). The section's zoom_in setting adds
       whole levels to that first fit; searches and locations fit as before. */
    if (!firstFitDone) {
      firstFitDone = true;
      var extra = parseInt(mapEl && mapEl.dataset.zoomIn, 10) || 0;
      if (extra && pts.length > 1 && !origin) {
        /* ...and never so far out that the world is shorter than the map,
           which leaves bands of empty ground above and below it (a phone's
           narrow map fits the globe at zoom 1, 512px tall in a 620px box). */
        var fill = Math.ceil(Math.log(map.getSize().y / 256) / Math.LN2);
        map.setZoom(Math.max(map.getZoom() + extra, fill), { animate: false });
      }
    }
  }

  function render(fit) {
    var list = ranked();
    renderRows(list);
    renderHeadings(list);
    renderMarkers(list);
    if (fit) fitTo(list);
  }

  function select(id, pan) {
    selectedId = selectedId === id ? null : id;
    render(false);
    var d = dealers.filter(function (x) { return x.id === selectedId; })[0];
    if (pan && d && hasPin(d) && map) map.setView([d.lat, d.lng], 12);
    if (selectedId && map) {
      markers.forEach(function (m) { if (m._dealerId === selectedId) m.openPopup(); });
    }
  }

  function initMap() {
    if (!window.L || !mapEl) return;
    var sb = (mapEl.dataset.startBounds || '').split(',').map(Number);
    if (sb.length === 4 && sb.every(isFinite)) startBounds = window.L.latLngBounds([sb[2], sb[1]], [sb[0], sb[3]]);
    /* zoomSnap 0 only with a fixed opening frame, so that frame can fit exactly;
       every other map keeps Leaflet's whole-level default. */
    map = window.L.map(mapEl, { scrollWheelZoom: false, zoomSnap: startBounds ? 0 : 1 }).setView([39.5, -98.35], 4);
    window.L.tileLayer(mapEl.dataset.tileUrl || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: mapEl.dataset.attribution || '© OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);
  }

  function boot() {
    rowsEl = document.querySelector('[data-dealer-rows]');
    mapEl = document.querySelector('[data-dealer-map]');
    if (!rowsEl) return;

    listHeadingEl = document.querySelector('[data-dealer-listheading]');
    distHeadingEl = document.querySelector('[data-dealer-distheading]');
    resultLineEl = document.querySelector('[data-dealer-resultline]');
    farNoteEl = document.querySelector('[data-dealer-farnote]');
    searchEl = document.querySelector('[data-dealer-search]');

    dealers = parseDealers();
    initMap();

    if (searchEl) {
      geoBase = searchEl.getAttribute('data-geo-base') || '';
      searchEl.addEventListener('input', function () {
        selectedId = null;
        typed = false; origin = geoOrigin;
        var q = searchEl.value.trim(), mine = ++seq;
        render(true);
        if (!q) return;
        resolvePlace(q).then(function (p) {
          if (mine !== seq || !p) return;
          typed = true; origin = p; render(true);
        });
      });
    }

    var locate = document.querySelector('[data-dealer-locate]');
    if (locate) {
      locate.addEventListener('click', function () {
        if (!navigator.geolocation) { resultLineEl.textContent = 'Location is not available in this browser.'; return; }
        resultLineEl.textContent = 'Finding you…';
        navigator.geolocation.getCurrentPosition(
          function (pos) {
            origin = geoOrigin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            typed = false;
            selectedId = null;
            render(true);
          },
          function () { resultLineEl.textContent = 'Could not get your location.'; },
          { timeout: 10000 }
        );
      });
    }

    var reset = document.querySelector('[data-dealer-reset]');
    if (reset) {
      reset.addEventListener('click', function () {
        origin = geoOrigin = null; typed = false; selectedId = null;
        if (searchEl) searchEl.value = '';
        firstFitDone = false; // reset means the opening view, zoom_in included
        render(true);
      });
    }

    render(true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
